import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Select from '../../../components/ui/Select';
import { fieldMappingService } from '../../../services/wearablesService';
import { useAuth } from '../../../contexts/AuthContext';

const FieldMappingPanel = ({ devices }) => {
  const { user } = useAuth();
  const [mappings, setMappings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      loadMappings();
    }
  }, [user]);

  const loadMappings = async () => {
    const { data, error } = await fieldMappingService?.getFieldMappings(user?.id);
    if (!error && data) {
      setMappings(data);
    }
  };

  const wearableMetrics = [
    { value: 'heart_rate', label: 'Heart Rate', icon: 'Heart' },
    { value: 'steps', label: 'Steps', icon: 'Footprints' },
    { value: 'calories', label: 'Calories', icon: 'Flame' },
    { value: 'sleep_hours', label: 'Sleep Hours', icon: 'Moon' },
    { value: 'active_minutes', label: 'Active Minutes', icon: 'Clock' },
    { value: 'distance', label: 'Distance', icon: 'MapPin' }
  ];

  const appFields = [
    { value: 'daily_steps', label: 'Daily Steps Goal', category: 'Activity' },
    { value: 'daily_calories', label: 'Daily Calories Burned', category: 'Activity' },
    { value: 'heart_rate_avg', label: 'Average Heart Rate', category: 'Health' },
    { value: 'sleep_duration', label: 'Sleep Duration', category: 'Sleep' },
    { value: 'workout_duration', label: 'Workout Duration', category: 'Fitness' },
    { value: 'distance_traveled', label: 'Distance Traveled', category: 'Activity' }
  ];

  const handleAddMapping = async () => {
    const newMapping = {
      wearableMetric: 'heart_rate',
      appField: 'heart_rate_avg',
      autoSync: true
    };

    const { data, error } = await fieldMappingService?.saveFieldMapping(newMapping);
    if (!error && data) {
      setMappings([...mappings, data]);
    }
  };

  const handleUpdateMapping = async (mappingId, updates) => {
    const updatedMappings = mappings?.map(m => 
      m?.id === mappingId ? { ...m, ...updates } : m
    );
    setMappings(updatedMappings);

    await fieldMappingService?.saveFieldMapping({ id: mappingId, ...updates });
  };

  const handleDeleteMapping = async (mappingId) => {
    await fieldMappingService?.deleteFieldMapping(mappingId);
    setMappings(mappings?.filter(m => m?.id !== mappingId));
  };

  return (
    <div className="bg-card rounded-xl p-6 elevation-2 h-full">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-success/10 rounded-lg">
            <Icon name="GitBranch" size={24} className="text-success" />
          </div>
          <div>
            <h2 className="text-2xl font-semibold text-foreground">Field Mapping</h2>
            <p className="text-sm text-muted-foreground">Configure automatic data population</p>
          </div>
        </div>
        <Button
          onClick={handleAddMapping}
          size="sm"
          className="flex items-center space-x-2"
        >
          <Icon name="Plus" size={16} />
          <span>Add Mapping</span>
        </Button>
      </div>
      <div className="space-y-4 max-h-[600px] overflow-y-auto">
        {mappings?.length === 0 ? (
          <div className="text-center py-12">
            <Icon name="GitBranch" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-sm text-muted-foreground mb-4">No field mappings configured</p>
            <Button onClick={handleAddMapping} size="sm">
              Create First Mapping
            </Button>
          </div>
        ) : (
          mappings?.map((mapping, index) => (
            <motion.div
              key={mapping?.id || index}
              className="bg-muted/30 rounded-xl p-4 border border-wellness-light"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="space-y-4">
                {/* Wearable Metric Selection */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-2">
                    Wearable Metric
                  </label>
                  <div className="grid grid-cols-3 gap-2">
                    {wearableMetrics?.map((metric) => (
                      <button
                        key={metric?.value}
                        type="button"
                        onClick={() => handleUpdateMapping(mapping?.id, { wearableMetric: metric?.value })}
                        className={`flex items-center space-x-2 p-2 rounded-lg border text-xs transition-all ${
                          mapping?.wearableMetric === metric?.value
                            ? 'border-primary bg-primary/10 text-primary' :'border-wellness-light text-muted-foreground hover:border-primary/50'
                        }`}
                      >
                        <Icon name={metric?.icon} size={14} />
                        <span>{metric?.label}</span>
                      </button>
                    ))}
                  </div>
                </div>

                {/* Mapping Arrow */}
                <div className="flex items-center justify-center">
                  <Icon name="ArrowDown" size={20} className="text-primary" />
                </div>

                {/* App Field Selection */}
                <div>
                  <label className="block text-xs font-medium text-muted-foreground mb-2">
                    App Field
                  </label>
                  <Select
                    value={mapping?.appField}
                    onChange={(e) => handleUpdateMapping(mapping?.id, { appField: e?.target?.value })}
                    className="w-full"
                  >
                    <option value="">Select field...</option>
                    {appFields?.map((field) => (
                      <option key={field?.value} value={field?.value}>
                        {field?.label} ({field?.category})
                      </option>
                    ))}
                  </Select>
                </div>

                {/* Auto Sync Toggle */}
                <div className="flex items-center justify-between">
                  <label className="flex items-center space-x-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={mapping?.autoSync ?? true}
                      onChange={(e) => handleUpdateMapping(mapping?.id, { autoSync: e?.target?.checked })}
                      className="w-4 h-4 rounded border-wellness-light text-primary focus:ring-primary"
                    />
                    <span className="text-xs text-foreground">Auto-sync enabled</span>
                  </label>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handleDeleteMapping(mapping?.id)}
                    className="text-error hover:bg-error/10"
                  >
                    <Icon name="Trash2" size={14} />
                  </Button>
                </div>
              </div>
            </motion.div>
          ))
        )}
      </div>
      {/* Mapping Info */}
      <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={18} className="text-primary flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-xs font-medium text-foreground mb-1">Field Mapping Guide</p>
            <p className="text-xs text-muted-foreground">
              Map wearable metrics to app fields for automatic data population. Enable auto-sync to update fields in real-time.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FieldMappingPanel;
